import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { SmsMessagesSection } from '../components/SmsMessagesSection.tsx';
import { Icon } from '../components/Icon.tsx';
import { t } from '../lib/strings.ts';

interface OrgSettingsData {
  headerImagePath: string | null;
  footerImagePath: string | null;
  footerText: string | null;
  sms: { username: string | null; enabled: boolean; passwordSet: boolean };
}

export function OrgSettings(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Org-management authority is role-based (no dedicated org-admin role in wappro):
  // ADMIN / SYS_ADMIN can manage the org. Mirrors the Sidebar/BottomNav nav gate so
  // the page never bounces a user who can see the Settings link.
  const canManageOrg = !!user && (user.isOrgAdmin || user.role === 'SYS_ADMIN' || user.role === 'ADMIN');

  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadingSide, setUploadingSide] = useState<'header' | 'footer' | null>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const footerInputRef = useRef<HTMLInputElement>(null);

  const [smsUsername, setSmsUsername] = useState<string>('');
  const [smsPassword, setSmsPassword] = useState<string>('');
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsSaved, setSmsSaved] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [smsTestTo, setSmsTestTo] = useState<string>('');
  const [smsTestSending, setSmsTestSending] = useState(false);
  const [smsTestResult, setSmsTestResult] = useState<string | null>(null);
  const [smsTestError, setSmsTestError] = useState<string | null>(null);

  // Guard — non-admins get redirected
  useEffect(() => {
    if (user && !canManageOrg) {
      void navigate({ to: '/' });
    }
  }, [user, canManageOrg, navigate]);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['org-settings'],
    queryFn: async () => {
      const r = await api.get<OrgSettingsData>('/org-settings');
      return r.data;
    },
    enabled: canManageOrg,
  });

  // Sync SMS fields from query data (password is never sent by the server —
  // passwordSet is the only signal; the input stays empty and shows a placeholder).
  useEffect(() => {
    if (settings) {
      setSmsUsername(settings.sms.username ?? '');
      setSmsEnabled(settings.sms.enabled);
      setSmsPassword('');
    }
  }, [settings]);


  const handleUploadImage = async (side: 'header' | 'footer', file: File) => {
    setUploadError(null);
    if (file.size > 2 * 1024 * 1024) {
      setUploadError(t.orgSettings.errorFileSize);
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setUploadError(t.orgSettings.errorFileType);
      return;
    }
    setUploadingSide(side);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(',')[1]!);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.put(`/org-settings/${side}`, { dataBase64: base64, mime: file.type });
      await qc.invalidateQueries({ queryKey: ['org-settings'] });
    } catch {
      setUploadError(t.orgSettings.errorSave);
    } finally {
      setUploadingSide(null);
    }
  };

  const handleSaveSms = async () => {
    setSmsError(null);
    setSmsSaved(false);
    setSmsSaving(true);
    try {
      const body: { username: string; enabled: boolean; password?: string } = {
        username: smsUsername,
        enabled: smsEnabled,
      };
      if (smsPassword) body.password = smsPassword;
      await api.put('/org-settings/sms', body);
      await qc.invalidateQueries({ queryKey: ['org-settings'] });
      setSmsSaved(true);
      setTimeout(() => setSmsSaved(false), 3000);
    } catch {
      setSmsError(t.orgSettings.smsErrorSave);
    } finally {
      setSmsSaving(false);
    }
  };

  const handleSendTestSms = async () => {
    setSmsTestError(null);
    setSmsTestResult(null);
    setSmsTestSending(true);
    try {
      const r = await api.post<{ ok: boolean; messageId?: string; error?: string }>('/org-settings/sms/test', { to: smsTestTo });
      if (r.data.ok) {
        setSmsTestResult(t.orgSettings.smsTestOk(r.data.messageId ?? ''));
      } else {
        setSmsTestError(r.data.error ?? t.orgSettings.smsTestError);
      }
    } catch {
      setSmsTestError(t.orgSettings.smsTestError);
    } finally {
      setSmsTestSending(false);
    }
  };

  if (!canManageOrg) {
    return <div className="page" />;
  }

  if (isLoading) {
    return (
      <div className="page">
        <p className="status-label text-muted-foreground" role="status">{t.orgSettings.loading}</p>
      </div>
    );
  }

  const hasHeaderImage = !!settings?.headerImagePath;
  const hasFooterImage = !!settings?.footerImagePath;

  return (
    <div className="page space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-ink">{t.orgSettings.heading}</h1>
      </header>

      {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

      <p className="text-sm text-muted-foreground">{t.orgSettings.letterheadHint}</p>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Header image */}
        <section className="card-paper space-y-3 p-4">
          <h2 className="status-label text-muted-foreground">{t.orgSettings.headerImageLabel}</h2>
          <p className="text-sm text-muted-foreground">
            {hasHeaderImage ? t.orgSettings.currentImage : t.orgSettings.noImage}
          </p>
          <input
            ref={headerInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="block text-sm"
            aria-label={t.orgSettings.ariaUploadHeader}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUploadImage('header', file);
              e.target.value = '';
            }}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => headerInputRef.current?.click()}
              disabled={uploadingSide === 'header'}
              className="btn-primary btn-sm"
            >
              <Icon name="upload" size={16} />
              {uploadingSide === 'header' ? t.orgSettings.saving : t.orgSettings.uploadBtn}
            </button>
          </div>
          <p className="text-xs text-muted-foreground"><span dir="ltr">1123 × 200 px</span> · {t.orgSettings.uploadHint}</p>
        </section>

        {/* Footer image */}
        <section className="card-paper space-y-3 p-4">
          <h2 className="status-label text-muted-foreground">{t.orgSettings.footerImageLabel}</h2>
          <p className="text-sm text-muted-foreground">
            {hasFooterImage ? t.orgSettings.currentImage : t.orgSettings.noImage}
          </p>
          <input
            ref={footerInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="block text-sm"
            aria-label={t.orgSettings.ariaUploadFooter}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUploadImage('footer', file);
              e.target.value = '';
            }}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => footerInputRef.current?.click()}
              disabled={uploadingSide === 'footer'}
              className="btn-primary btn-sm"
            >
              <Icon name="upload" size={16} />
              {uploadingSide === 'footer' ? t.orgSettings.saving : t.orgSettings.uploadBtn}
            </button>
          </div>
          <p className="text-xs text-muted-foreground"><span dir="ltr">1123 × 100 px</span> · {t.orgSettings.uploadHint}</p>
        </section>
      </div>

      {/* SMS (Dhiraagu) */}
      <section className="card-paper p-5 space-y-3">
        <h2 className="font-semibold text-ink">{t.orgSettings.smsHeading}</h2>

        <div className="space-y-1">
          <label htmlFor="sms-username" className="text-sm text-muted-foreground">{t.orgSettings.smsUsernameLabel}</label>
          <input
            id="sms-username"
            type="text"
            dir="ltr"
            className="input"
            value={smsUsername}
            onChange={(e) => setSmsUsername(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="sms-password" className="text-sm text-muted-foreground">{t.orgSettings.smsPasswordLabel}</label>
          <input
            id="sms-password"
            type="password"
            dir="ltr"
            className="input"
            value={smsPassword}
            onChange={(e) => setSmsPassword(e.target.value)}
            placeholder={settings?.sms.passwordSet ? t.orgSettings.smsPasswordPlaceholderSaved : t.orgSettings.smsPasswordPlaceholderEmpty}
            autoComplete="new-password"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded accent-primary cursor-pointer"
            checked={smsEnabled}
            onChange={(e) => setSmsEnabled(e.target.checked)}
          />
          <span className="text-sm">{t.orgSettings.smsEnabledLabel}</span>
        </label>

        {smsError && <p className="text-sm text-destructive">{smsError}</p>}
        {smsSaved && <p className="text-sm text-success">{t.orgSettings.smsSavedOk}</p>}
        <button
          onClick={() => void handleSaveSms()}
          disabled={smsSaving}
          className="btn-primary btn-sm"
        >
          {smsSaving ? t.orgSettings.saving : t.orgSettings.smsSaveBtn}
        </button>

        <div className="pt-3 border-t border-border space-y-3">
          <h3 className="text-sm font-medium text-ink">{t.orgSettings.smsTestHeading}</h3>
          <input
            type="tel"
            dir="ltr"
            className="input"
            value={smsTestTo}
            onChange={(e) => setSmsTestTo(e.target.value)}
            placeholder={t.orgSettings.smsTestPhonePlaceholder}
          />
          {smsTestError && <p className="text-sm text-destructive">{smsTestError}</p>}
          {smsTestResult && <p className="text-sm text-success">{smsTestResult}</p>}
          <button
            onClick={() => void handleSendTestSms()}
            disabled={smsTestSending || !smsTestTo}
            className="btn-secondary btn-sm"
          >
            {smsTestSending ? t.orgSettings.smsTestSending : t.orgSettings.smsTestBtn}
          </button>
        </div>
      </section>

      {/* Vendor SMS message templates */}
      <SmsMessagesSection />
    </div>
  );
}
